import { Helmet } from "react-helmet-async";
import ReactHeader from "./reactHeader";
import FooterSection from "./footerSection";
import './privacyPolicy_subscriptionAgreement.css';
import AnimatedBlock from "./animatedBlock";

const PrivacyPolicySection = () => {
    return (
        <section className="page-section section-style-privacyPolicy">
            <div className="content-wrapper">
                <div className="content">
                    <div className="fluid-engine">
                        <div className="heading-block">
                            <AnimatedBlock>
                                <h1 className="green-title">Privacy Policy</h1>
                            </AnimatedBlock>
                        </div>

                        <div className="text-block">
                            <div className="container">
                                <AnimatedBlock>
                                <section>
                                    <h2>Overview</h2>
                                    <p>
                                        Khor Developer LLC ("Khor Developer," "we," "us," or "our") values your privacy and is committed to protecting the personal information you provide to us. This Privacy Policy outlines how we collect, use, disclose, and safeguard personal data in two distinct contexts: (1) your use of our website at <a href="http://www.khordeveloper.com/">www.khordeveloper.com</a> (the "Site"), and (2) your use of the Khor Developer Software, including the Excel Add-In (the "Product"). These contexts involve different data, described separately below.
                                    </p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Information We Collect — Product / Account Use</h2>
                                    <p>
                                        When you use the Product, we collect the account information needed to authenticate you and administer your license: your name and work email address, collected via Microsoft Azure AD B2C when you sign in with your organization's Microsoft or Google account. We do not collect or store passwords; authentication is handled by Microsoft or Google.
                                    </p>
                                    <p>
                                        The Product runs locally on your device. Deal, model and property data you work with stays on your device and is not transmitted to us, with one exception:
                                    </p>
                                    <p>
                                        <strong>Khor AI (BETA).</strong> If Khor AI is enabled for you and you choose to use it, the full contents of the documents you select are sent through our relay service (hosted on Microsoft Azure) to Anthropic's Claude API for processing. Our relay does not store or log document contents. For users with Khor AI enabled, we keep a monthly usage record (user ID, email address, usage amounts, and first and last use) and basic request and error logs. Khor AI is off by default and is enabled only for specific users at their organization's request.
                                    </p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Information We Collect — Site Use</h2>
                                    <p>Separately, if you visit our Site, fill out a contact or demo request form, or communicate with us directly (e.g., by email), we may collect information you provide voluntarily, such as your name, company name, and contact details. We do not collect or store payment card information ourselves; payment is processed directly by Stripe, and your payment details are governed by Stripe's own privacy policy (see "How We Share Your Information" below).</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Automatically Collected Information</h2>
                                    <p>When you visit our Site, certain details about your device and browsing activity may be collected automatically. This can include your IP address (which may indicate your general location), browser type, device type, access times, and pages visited.</p>
                                    <p>Our website is hosted on Microsoft Azure. As part of standard web hosting, Azure may automatically collect certain technical data such as request logs and IP addresses. This data is handled in accordance with <a href="https://privacy.microsoft.com" target="_blank" rel="noopener noreferrer">Microsoft’s Privacy Policy</a>.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>How We Use This Information</h2>
                                    <p>We use the information described above to:</p>
                                    <ul>
                                        <li>Authenticate users and administer Product licenses.</li>
                                        <li>Provide the optional Khor AI feature to users it's enabled for, and track their usage against their license.</li>
                                        <li>Operate and improve the Site, including responding to inquiries.</li>
                                        <li>Process payments (via Stripe) and maintain business records.</li>
                                        <li>Enforce our agreements, detect fraud or misuse, and comply with legal obligations.</li>
                                        <li>Communicate with you about the Site and Product, with your consent where required.</li>
                                    </ul>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>How We Share Your Information</h2>
                                    <p>We share personal data only with third-party service providers who help us operate our business:</p>
                                    <ul>
                                        <li><strong>Stripe</strong>, for payment processing (governed by <a href="https://stripe.com/privacy">Stripe’s Privacy Policy</a>).</li>
                                        <li><strong>Microsoft Azure</strong>, for Site hosting, Product authentication (Azure AD B2C), and our Khor AI relay service and its usage records (governed by <a href="https://privacy.microsoft.com" target="_blank" rel="noopener noreferrer">Microsoft’s Privacy Policy</a>). We do not store passwords; authentication is handled by Microsoft.</li>
                                        <li><strong>Google</strong>, as an identity provider for users who sign in with a Google account (governed by <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google’s Privacy Policy</a>).</li>
                                        <li><strong>Anthropic</strong>, to process documents that users choose to send to Khor AI. Anthropic deletes API inputs and outputs within 30 days by default, except where retention is needed to enforce its Usage Policy or is required by law, and does not use this data to train its models by default (governed by Anthropic's <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noopener noreferrer">Commercial Terms</a> and <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>).</li>
                                    </ul>
                                    <p>We may also disclose personal information:</p>
                                    <ul>
                                        <li>In connection with business transactions such as a sale or merger.</li>
                                        <li>To comply with legal obligations or enforce our agreements.</li>
                                        <li>To protect our rights, prevent fraud, or ensure security.</li>
                                    </ul>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Use of De-Identified Data</h2>
                                    <p>We may create anonymized or de-identified data from Site usage and account information (e.g., aggregated Site traffic patterns) to improve our website and services. We do not collect, aggregate or de-identify deal, model or property data, including documents sent to Khor AI. Any de-identified data we do create will not personally identify you.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Legal Basis for Processing</h2>
                                    <p>We process personal information based on:</p>
                                    <ul>
                                        <li>The need to fulfill our contractual obligations to you.</li>
                                        <li>Our legitimate business interests in providing and improving the Site and Product.</li>
                                        <li>Your explicit consent, where applicable.</li>
                                        <li>Compliance with legal requirements.</li>
                                    </ul>
                                    <p>If we process your data based on consent, you may withdraw your consent at any time. However, withdrawing consent may affect your ability to use certain aspects of the Site and Product.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Third-Party Links</h2>
                                    <p>Our Site may contain links to external websites that we do not control. We are not responsible for how those third parties handle your data, and we encourage you to review their privacy policies before interacting with their services.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Data Security</h2>
                                    <p>We take reasonable security measures to protect the personal information described above. No system is foolproof, and we cannot guarantee absolute security. Access to information is limited to necessary personnel and service providers. Deal, model and property data in the Product remains on your device, and you are responsible for its security there. Documents sent to Khor AI are transmitted over encrypted connections and are not stored by us.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Data Retention</h2>
                                    <p>Account and usage records are kept for the life of your subscription and deleted within 30 days of cancellation or upon request. Request and error logs are deleted automatically after 90 days. Information from Site inquiries is kept as long as needed to respond and for our business records. Once no longer needed, data is securely deleted or anonymized.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Your Rights</h2>
                                    <p>You have rights regarding your personal data, including:</p>
                                    <ul>
                                        <li>Requesting access to the data we hold about you.</li>
                                        <li>Correcting inaccuracies in your personal information.</li>
                                        <li>Requesting deletion of your data under certain conditions.</li>
                                        <li>Restricting or objecting to certain processing activities.</li>
                                        <li>Requesting a copy of your data in a structured format.</li>
                                    </ul>
                                    <p>If you are a resident of the EEA or California, you may have additional rights under GDPR or CCPA. Contact us at <a href="mailto:support@khordeveloper.com" target="_blank" rel="noopener noreferrer">support@khordeveloper.com</a>.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Policy Updates</h2>
                                    <p>We may update this Privacy Policy periodically. Changes will be posted here with an updated effective date.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section>
                                    <h2>Contact Us</h2>
                                    <p>For questions, inquiries, or requests, please contact us at <a href="mailto:support@khordeveloper.com" target="_blank" rel="noopener noreferrer">support@khordeveloper.com</a>.</p>
                                </section>
                                </AnimatedBlock>

                                <AnimatedBlock>
                                <section style={{ marginTop: "24px" }}>
                                    <p>Last Updated: October 22, 2026</p>
                                </section>
                                </AnimatedBlock>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

function PrivacyPolicy() {
    return (
        <div className="App">
            <Helmet>
                <title>Privacy Policy | Khor Developer</title>
                <meta name="description" content="Khor Developer's privacy policy explains how we collect, use, and protect your personal data when using our CRE underwriting software and website." />
                <link rel="canonical" href="https://www.khordeveloper.com/privacy-policy" />
            </Helmet>
            <ReactHeader />
            <PrivacyPolicySection />
            <FooterSection />
        </div>
    );
}

export default PrivacyPolicy;
